import { useState } from 'react';

interface Service {
  icon: string;
  title: string;
  description: string;
  included: string[];
}

export default function Services() {
  const [expandedCard, setExpandedCard] = useState<number | null>(null);

  const services: Service[] = [
    {
      icon: 'ri-home-4-line',
      title: 'House / Exterior Wash',
      description: 'Complete exterior cleaning that restores your home\'s curb appeal',
      included: ['Pre-treat stains', 'Surface-safe wash method', 'Final rinse + detail edges']
    },
    {
      icon: 'ri-road-map-line',
      title: 'Driveway & Walkway Cleaning',
      description: 'Remove oil stains, dirt, and grime from concrete surfaces',
      included: ['Pre-treat stains', 'Surface-safe wash method', 'Final rinse + detail edges']
    },
    {
      icon: 'ri-layout-grid-line',
      title: 'Patio & Pool Deck Cleaning',
      description: 'Safe cleaning for outdoor living spaces and pool areas',
      included: ['Pre-treat stains', 'Surface-safe wash method', 'Final rinse + detail edges']
    },
    {
      icon: 'ri-home-smile-line',
      title: 'Roof Soft Wash',
      description: 'Gentle cleaning method that protects your roof while removing algae',
      included: ['Pre-treat stains', 'Surface-safe wash method', 'Final rinse + detail edges']
    },
    {
      icon: 'ri-separator',
      title: 'Fence & Wall Cleaning',
      description: 'Restore the look of wood, vinyl, or concrete fences and walls',
      included: ['Pre-treat stains', 'Surface-safe wash method', 'Final rinse + detail edges']
    },
    {
      icon: 'ri-grid-line',
      title: 'Pavers & Concrete Cleaning',
      description: 'Deep clean pavers and concrete to reveal original colors',
      included: ['Pre-treat stains', 'Surface-safe wash method', 'Final rinse + detail edges']
    },
    {
      icon: 'ri-contrast-drop-2-line',
      title: 'Gutter Brightening',
      description: 'Remove black streaks and restore gutter appearance',
      included: ['Pre-treat stains', 'Surface-safe wash method', 'Final rinse + detail edges']
    },
    {
      icon: 'ri-delete-bin-line',
      title: 'Trash Can Cleaning',
      description: 'Sanitize and deodorize trash cans for a fresh, clean home',
      included: ['Pre-treat stains', 'Surface-safe wash method', 'Final rinse + detail edges']
    },
    {
      icon: 'ri-store-2-line',
      title: 'Commercial Storefront Cleaning',
      description: 'Professional cleaning for businesses and commercial properties',
      included: ['Pre-treat stains', 'Surface-safe wash method', 'Final rinse + detail edges']
    }
  ];

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section id="services" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-extrabold text-blue-900 mb-4">
            Our Services
          </h2>
          <div className="w-16 h-1 bg-gradient-to-r from-cyan-400 to-cyan-500 mx-auto mb-6"></div>
          <p className="text-lg text-blue-700 max-w-2xl mx-auto">
            Comprehensive pressure cleaning solutions for every surface
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {services.map((service, index) => (
            <div
              key={index}
              className="bg-gradient-to-br from-cyan-50 to-blue-50/30 rounded-2xl p-6 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-pointer border border-cyan-100"
              onClick={() => setExpandedCard(expandedCard === index ? null : index)}
            >
              {/* Icon */}
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-cyan-500 rounded-full flex items-center justify-center mb-4 mx-auto">
                <i className={`${service.icon} text-white text-3xl`}></i>
              </div>

              {/* Title */}
              <h3 className="text-lg font-bold text-blue-900 text-center mb-2">
                {service.title}
              </h3>

              {/* Description */}
              <p className="text-sm text-blue-700 text-center mb-4">
                {service.description}
              </p>

              {/* Expandable Section */}
              <div className="border-t border-cyan-200 pt-4">
                <button
                  className="flex items-center justify-center w-full text-orange-500 font-semibold text-sm hover:text-orange-600 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedCard(expandedCard === index ? null : index);
                  }}
                >
                  What's included
                  <i className={`ml-2 transition-transform duration-300 ${expandedCard === index ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
                </button>

                {expandedCard === index && (
                  <ul className="mt-4 space-y-2">
                    {service.included.map((item, idx) => (
                      <li key={idx} className="flex items-start text-sm text-blue-700">
                        <i className="ri-check-line text-cyan-500 mr-2 mt-0.5"></i>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center">
          <button
            onClick={scrollToContact}
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold rounded-full hover:shadow-xl hover:scale-105 transition-all duration-300 whitespace-nowrap"
          >
            Request a Quote
            <i className="ri-arrow-right-line ml-2"></i>
          </button>
        </div>
      </div>
    </section>
  );
}
